import { RefObject, useLayoutEffect, useState } from 'react';

// Whether an element's content is cut off by its own box (a line clamp), so a
// "Show more" is worth offering. Checked after layout, whenever the element resizes, once web fonts have
// loaded, and whenever `deps` change (#1008). The extra pixel absorbs sub-pixel rounding.
export function useOverflowing<T extends HTMLElement>(ref: RefObject<T | null>, deps: unknown[]): boolean {
  const [overflowing, setOverflowing] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let live = true;
    const check = () => { if (live) setOverflowing(el.scrollHeight > el.clientHeight + 1); };
    check();
    // A web font that arrives late can push the text past the clamp without resizing
    // the box, so measure again once fonts have loaded.
    void document.fonts?.ready.then(check);
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(check);
      observer.observe(el);
    } else {
      window.addEventListener('resize', check);
    }
    return () => {
      live = false;
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', check);
    };
    // The caller's deps decide when to measure again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return overflowing;
}
